import { useEffect, useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, Calendar as CalendarIcon, Clock, User, Loader2, CheckCircle2, Layers, Brain, ShieldCheck, Boxes, Landmark, GraduationCap } from 'lucide-react';
import { toast } from 'sonner';
import { bookingCopy, serviceDetails } from '@/i18n.services';
import { postBooking, getBookingAvailability } from '@/lib/api';
import SEO from '@/components/SEO';

const SERVICE_ICONS = {
  platforms: Layers,
  ai: Brain,
  security: ShieldCheck,
  blockchain: Boxes,
  banking: Landmark,
  cauracademy: GraduationCap,
};

// Generate next N business days starting today
function nextBusinessDays(n) {
  const out = [];
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  while (out.length < n) {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) {
      out.push(new Date(d));
    }
    d.setDate(d.getDate() + 1);
  }
  return out;
}

function fmtDateKey(d) {
  // YYYY-MM-DD
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const TIME_SLOTS = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
  '16:00', '16:30', '17:00',
];

const BookingPage = ({ lang }) => {
  const copy = bookingCopy[lang];
  const services = serviceDetails[lang];
  const location = useLocation();
  const navigate = useNavigate();

  const [step, setStep] = useState(() => (location.state?.service ? 1 : 0));
  const [service, setService] = useState(location.state?.service || null);
  const [date, setDate] = useState(null);  // Date instance
  const [time, setTime] = useState(null);  // HH:MM
  const [booked, setBooked] = useState([]); // booked times for selected date
  const [checkingAvail, setCheckingAvail] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', company: '', phone: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [bookingId, setBookingId] = useState(null);

  const days = useMemo(() => nextBusinessDays(14), []);

  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, [step]);

  // Load availability when date changes
  useEffect(() => {
    if (!date) return;
    const key = fmtDateKey(date);
    setCheckingAvail(true);
    getBookingAvailability(key)
      .then((d) => setBooked(d.booked_times || []))
      .catch(() => setBooked([]))
      .finally(() => setCheckingAvail(false));
  }, [date]);

  const canNext = () => {
    if (step === 0) return !!service;
    if (step === 1) return !!date;
    if (step === 2) return !!time;
    if (step === 3) return form.name.trim() && form.email.trim();
    return true;
  };

  const onSubmit = async () => {
    if (!service || !date || !time || !form.name || !form.email) return;
    setSubmitting(true);
    try {
      const res = await postBooking({
        service,
        date: fmtDateKey(date),
        time,
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        phone: form.phone.trim() || null,
        notes: form.notes.trim() || null,
        language: lang,
      });
      setBookingId(res.id);
      setStep(4);
    } catch (e) {
      toast.error(copy.errors.submit);
    } finally {
      setSubmitting(false);
    }
  };

  const stepIndex = Math.min(step, 4);

  return (
    <main className="bg-grid relative min-h-[80vh] py-12 sm:py-16" data-testid="booking-page">
      <SEO
        lang={lang}
        path="/agenda"
        title={lang === 'es' ? 'Agendar reunión confidencial' : 'Schedule confidential meeting'}
        description={lang === 'es'
          ? 'Agenda una reunión privada con el equipo de Cauratech. NDA-ready, sin presión comercial.'
          : 'Book a private meeting with the Cauratech team. NDA-ready, no sales pressure.'}
      />
      <div className="orb float-slow" style={{ width: 380, height: 380, background: 'rgba(5,126,243,.25)', top: '-80px', right: '-80px' }} />

      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back */}
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white transition-colors" data-testid="booking-back-home">
          {copy.nav_back}
        </Link>

        {/* Header */}
        <div className="mt-6 text-center">
          <div className="kicker-mono mb-3">// {copy.title}</div>
          <h1 className="font-display neon-title text-4xl sm:text-5xl lg:text-6xl font-[700] tracking-[-0.02em]">{copy.title}</h1>
          <p className="mt-4 max-w-xl mx-auto text-white/70">{copy.subtitle}</p>
        </div>

        {/* Progress */}
        <div className="mt-10 mb-8" data-testid="booking-progress">
          <div className="flex items-center justify-between gap-2">
            {copy.step_labels.map((label, i) => (
              <div key={label} className="flex-1 flex flex-col items-center">
                <div className={`h-8 w-8 rounded-full grid place-items-center text-xs font-mono-accent font-bold border transition-colors ${
                  i < stepIndex ? 'bg-[rgb(var(--brand-electric))] border-[rgb(var(--brand-electric))] text-white' :
                  i === stepIndex ? 'bg-[rgba(5,126,243,.15)] border-[rgb(var(--brand-electric))] text-white shadow-[var(--glow-electric)]' :
                  'bg-[rgb(var(--bg-2))] border-white/10 text-white/50'
                }`}>
                  {i < stepIndex ? <Check size={14} /> : i + 1}
                </div>
                <div className={`mt-2 kicker-mono text-[10px] ${i === stepIndex ? 'text-white' : 'text-white/40'}`}>{label}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 h-1 rounded-full bg-white/10 overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-[rgb(var(--brand-royal))] via-[rgb(var(--brand-electric))] to-[rgb(var(--brand-sky))]"
              animate={{ width: `${(stepIndex / 4) * 100}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>
        </div>

        {/* Step content */}
        <div className="gradient-border">
          <div className="inner p-6 sm:p-8 min-h-[360px]">
            <AnimatePresence mode="wait">
              {/* STEP 0: Service */}
              {step === 0 && (
                <motion.div key="s0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} data-testid="step-service">
                  <h2 className="font-display text-2xl font-[650] text-white">{copy.service_step.title}</h2>
                  <p className="mt-2 text-sm text-white/65">{copy.service_step.subtitle}</p>
                  <div className="mt-6 grid sm:grid-cols-2 gap-3">
                    {Object.values(services).map((s) => {
                      const Icon = SERVICE_ICONS[s.id] || Layers;
                      const selected = service === s.id;
                      return (
                        <button
                          key={s.id}
                          onClick={() => setService(s.id)}
                          className={`text-left rounded-xl border p-4 transition-colors ${
                            selected ? 'border-[rgb(var(--brand-electric))] bg-[rgba(5,126,243,.10)] shadow-[var(--glow-electric)]' : 'border-white/10 bg-[rgb(var(--bg-2))] hover:border-[rgba(5,126,243,.4)]'
                          }`}
                          data-testid={`booking-service-${s.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="h-9 w-9 rounded-lg bg-[rgba(5,126,243,.12)] border border-[rgba(5,126,243,.35)] flex items-center justify-center text-[rgb(var(--brand-electric))] shrink-0">
                              <Icon size={18} />
                            </div>
                            <div>
                              <div className="font-display text-sm font-[650] text-white">{lang === 'es' ? s.kicker.replace('// ', '') : s.kicker.replace('// ', '')}</div>
                              <div className="text-xs text-white/60 mt-0.5 line-clamp-2">{s.subtitle}</div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* STEP 1: Date */}
              {step === 1 && (
                <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} data-testid="step-date">
                  <div className="flex items-center gap-2 mb-2">
                    <CalendarIcon size={18} className="text-[rgb(var(--brand-electric))]" />
                    <h2 className="font-display text-2xl font-[650] text-white">{copy.date_step.title}</h2>
                  </div>
                  <p className="text-sm text-white/65">{copy.date_step.subtitle}</p>
                  <div className="mt-6 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2.5">
                    {days.map((d) => {
                      const key = fmtDateKey(d);
                      const selected = date && fmtDateKey(date) === key;
                      return (
                        <button
                          key={key}
                          onClick={() => setDate(d)}
                          className={`rounded-xl border p-3 flex flex-col items-center transition-colors ${
                            selected ? 'border-[rgb(var(--brand-electric))] bg-[rgba(5,126,243,.15)] text-white shadow-[var(--glow-electric)]' : 'border-white/10 bg-[rgb(var(--bg-2))] text-white/80 hover:border-[rgba(5,126,243,.4)] hover:text-white'
                          }`}
                          data-testid={`booking-date-${key}`}
                        >
                          <span className="kicker-mono text-[10px]">{copy.days_short[d.getDay()]}</span>
                          <span className="font-display text-xl font-[700] mt-1">{d.getDate()}</span>
                          <span className="text-[10px] text-white/50 mt-0.5">{copy.months[d.getMonth()]}</span>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* STEP 2: Time */}
              {step === 2 && (
                <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} data-testid="step-time">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock size={18} className="text-[rgb(var(--brand-electric))]" />
                    <h2 className="font-display text-2xl font-[650] text-white">{copy.time_step.title}</h2>
                  </div>
                  <p className="text-sm text-white/65">{copy.time_step.subtitle}</p>
                  {checkingAvail && <div className="mt-4 text-sm text-white/50 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> ...</div>}
                  <div className="mt-6 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2.5">
                    {TIME_SLOTS.map((slot) => {
                      const isBooked = booked.includes(slot);
                      const selected = time === slot;
                      return (
                        <button
                          key={slot}
                          disabled={isBooked}
                          onClick={() => setTime(slot)}
                          className={`rounded-xl border py-3 text-sm font-semibold transition-colors ${
                            isBooked ? 'border-white/5 bg-white/[0.02] text-white/30 cursor-not-allowed line-through' :
                            selected ? 'border-[rgb(var(--brand-electric))] bg-[rgba(5,126,243,.15)] text-white shadow-[var(--glow-electric)]' :
                            'border-white/10 bg-[rgb(var(--bg-2))] text-white/85 hover:border-[rgba(5,126,243,.4)] hover:text-white'
                          }`}
                          data-testid={`booking-time-${slot}`}
                        >
                          {slot}
                          {isBooked && <div className="kicker-mono text-[8px] mt-0.5">{copy.time_step.unavailable}</div>}
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* STEP 3: Contact */}
              {step === 3 && (
                <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} data-testid="step-form">
                  <div className="flex items-center gap-2 mb-2">
                    <User size={18} className="text-[rgb(var(--brand-electric))]" />
                    <h2 className="font-display text-2xl font-[650] text-white">{copy.form_step.title}</h2>
                  </div>
                  <p className="text-sm text-white/65">{copy.form_step.subtitle}</p>
                  <div className="mt-6 grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="kicker-mono block mb-2 text-white/60">{copy.form_step.labels.name}</label>
                      <input className="field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={copy.form_step.placeholders.name} data-testid="booking-name" />
                    </div>
                    <div>
                      <label className="kicker-mono block mb-2 text-white/60">{copy.form_step.labels.email}</label>
                      <input type="email" className="field" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={copy.form_step.placeholders.email} data-testid="booking-email" />
                    </div>
                    <div>
                      <label className="kicker-mono block mb-2 text-white/60">{copy.form_step.labels.company}</label>
                      <input className="field" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder={copy.form_step.placeholders.company} data-testid="booking-company" />
                    </div>
                    <div>
                      <label className="kicker-mono block mb-2 text-white/60">{copy.form_step.labels.phone}</label>
                      <input className="field" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={copy.form_step.placeholders.phone} data-testid="booking-phone" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="kicker-mono block mb-2 text-white/60">{copy.form_step.labels.notes}</label>
                      <textarea className="field resize-none" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder={copy.form_step.placeholders.notes} data-testid="booking-notes" />
                    </div>
                  </div>
                  {/* summary */}
                  <div className="mt-6 rounded-xl border border-white/10 bg-[rgb(var(--bg-2))] p-4 text-sm" data-testid="booking-summary">
                    <div className="kicker-mono text-white/50 mb-2">{lang === 'es' ? 'Tu reserva' : 'Your booking'}</div>
                    <div className="text-white/80">
                      <strong className="text-white">{services[service]?.kicker.replace('// ', '')}</strong> · {' '}
                      {date && `${copy.days_short[date.getDay()]} ${date.getDate()} ${copy.months[date.getMonth()]} ${date.getFullYear()}`} · {time}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* STEP 4: Success */}
              {step === 4 && (
                <motion.div key="s4" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.4 }} className="text-center py-10" data-testid="step-success">
                  <div className="mx-auto h-16 w-16 rounded-full bg-[rgba(5,126,243,.15)] border border-[rgba(5,126,243,.4)] flex items-center justify-center text-[rgb(var(--brand-electric))] shadow-[var(--glow-electric)]">
                    <CheckCircle2 size={32} />
                  </div>
                  <h2 className="mt-6 font-display text-3xl font-[650] text-white">{copy.success.title}</h2>
                  <p className="mt-3 text-white/70">{copy.success.subtitle}</p>
                  {bookingId && <p className="mt-3 kicker-mono text-white/50">ID: {bookingId.slice(0, 8)}</p>}
                  <button onClick={() => navigate('/')} className="btn-primary mt-7 inline-flex" data-testid="booking-success-home">
                    {copy.success.cta}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Footer actions */}
        {step < 4 && (
          <div className="mt-6 flex items-center justify-between gap-3">
            <button
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              className="btn-ghost disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="booking-back"
            >
              <ArrowLeft size={15} /> {copy.actions.back}
            </button>
            {step < 3 ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                disabled={!canNext()}
                className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="booking-next"
              >
                {copy.actions.next} <ArrowRight size={15} />
              </button>
            ) : (
              <button
                onClick={onSubmit}
                disabled={!canNext() || submitting}
                className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="booking-submit"
              >
                {submitting ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                {copy.actions.submit}
              </button>
            )}
          </div>
        )}
      </div>
    </main>
  );
};

export default BookingPage;
